import {
  CheckIcon,
  ShareIcon,
  ViewGridIcon,
  XIcon,
} from '@heroicons/react/solid';
import Image from 'next/image';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import AutoPlayVideo from '@/components/media/AutoPlayVideo';
import { SimpleTooltip } from '@/components/ui/tooltip';

export interface ToolsNavTemplateInfo {
  /** Already-translated template name. */
  title: string;
  /** Example result — a clip when `isVideoPreview`, otherwise an image. */
  previewUrl: string;
  isVideoPreview?: boolean;
  /** Site-relative public page of the template. Omit to hide Share. */
  sharePath?: string | null;
  /** Opens the page's template gallery. Omit to hide the Templates button. */
  onBrowseTemplates?: () => void;
  /** Clears the template. Omit to hide the remove button. */
  onRemove?: () => void;
}

// The banner shows a thin horizontal slice of mostly-portrait previews, so the
// crop is biased toward where the subject's face usually sits: near the top of
// a still portrait, around the upper third of a moving clip.
const IMAGE_FOCUS = 'object-cover object-[center_12%]';
const VIDEO_FOCUS = 'object-cover object-[center_30%]';

// Frosted controls that stay legible on any frame of the preview.
const glassButton =
  'flex flex-shrink-0 items-center justify-center bg-black/30 text-white ring-1 ring-inset ring-white/20 backdrop-blur-md transition-colors hover:bg-black/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-white';

/**
 * Replaces the ToolsNav context card while the tool was opened from a
 * template: the template's own example fills the card, so the user keeps the
 * expected result in view while filling the form.
 */
const ToolsNavTemplate = ({
  title,
  previewUrl,
  isVideoPreview,
  sharePath,
  onBrowseTemplates,
  onRemove,
}: ToolsNavTemplateInfo) => {
  const { t } = useLocale('dashboard');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timeout = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeout);
  }, [copied]);

  // Phones get the native share sheet; desktops just copy the link, where the
  // OS share menu is more of a detour than a shortcut.
  const share = async () => {
    if (!sharePath) return;
    const url = new URL(sharePath, window.location.origin).toString();

    if (
      typeof navigator.share === 'function' &&
      window.matchMedia('(pointer: coarse)').matches
    ) {
      try {
        await navigator.share({ title, url });
        return;
      } catch (error) {
        if ((error as DOMException)?.name === 'AbortError') return;
      }
    }

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t('Link copied'));
    } catch {
      toast.error(t('Could not copy the link'));
    }
  };

  return (
    <div className='relative mt-2.5 h-36 overflow-hidden rounded-xl bg-slate-800 ring-1 ring-inset ring-black/5 dark:ring-white/10'>
      {isVideoPreview ? (
        <AutoPlayVideo
          src={previewUrl}
          className={`absolute inset-0 h-full w-full ${VIDEO_FOCUS}`}
        />
      ) : (
        <Image
          src={previewUrl}
          alt=''
          fill
          priority
          sizes='(max-width: 640px) 100vw, 480px'
          unoptimized={previewUrl.endsWith('.gif')}
          className={IMAGE_FOCUS}
        />
      )}

      <div className='pointer-events-none absolute inset-x-0 top-0 h-14 bg-gradient-to-b from-black/35 to-transparent' />
      <div className='pointer-events-none absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/80 via-black/35 to-transparent' />

      <div className='absolute right-2.5 top-2.5 flex items-center gap-1.5'>
        {sharePath && (
          <SimpleTooltip
            title={copied ? t('Link copied') : t('Share template')}
          >
            <button
              type='button'
              onClick={share}
              aria-label={t('Share template')}
              className={`${glassButton} h-7 w-7 rounded-full`}
            >
              {copied ? (
                <CheckIcon className='h-4 w-4' />
              ) : (
                <ShareIcon className='h-3.5 w-3.5' />
              )}
            </button>
          </SimpleTooltip>
        )}
        {onRemove && (
          <SimpleTooltip title={t('Remove template')}>
            <button
              type='button'
              onClick={onRemove}
              aria-label={t('Remove template')}
              className={`${glassButton} h-7 w-7 rounded-full`}
            >
              <XIcon className='h-4 w-4' />
            </button>
          </SimpleTooltip>
        )}
      </div>

      <div className='absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-3.5'>
        <h2 className='line-clamp-2 min-w-0 text-base font-semibold leading-snug tracking-tight text-white [text-shadow:0_1px_2px_rgb(0_0_0/0.4)]'>
          {title}
        </h2>
        {onBrowseTemplates && (
          <button
            type='button'
            onClick={onBrowseTemplates}
            className={`${glassButton} gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium`}
          >
            <ViewGridIcon className='h-4 w-4' />
            {t('Templates')}
          </button>
        )}
      </div>
    </div>
  );
};

export default ToolsNavTemplate;
